import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { apiError, can } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Plus, Pencil, Trash2, CalendarDays, Clock } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import MedicalAvailabilityPanel from "../components/MedicalAvailabilityPanel";
import { toast } from "sonner";

const COLORS = ["#60A5FA", "#6EE7B7", "#A78BFA", "#67E8F9", "#FCD34D", "#FCA5A5", "#5EEAD4", "#F9A8D4"];
const WEEKDAYS = ["Lunedì", "Martedì", "Mercoledì", "Giovedì", "Venerdì", "Sabato", "Domenica"];
const EMPTY = { first_name: "", last_name: "", phone: "", email: "", specialization: "", color: "#60A5FA", active: true, default_slot_duration_minutes: 20 };
const EMPTY_SHIFT = { type: "turno", weekday: "0", date: "", start: "08:00", end: "13:00", note: "", recurring: true };

export default function Physios({ category = "fisioterapista" }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [physios, setPhysios] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [shiftFor, setShiftFor] = useState(null);
  const [shiftForm, setShiftForm] = useState(EMPTY_SHIFT);
  const isMedico = category === "medico";
  const section = isMedico ? "medici" : "fisioterapisti";
  const canEdit = can(user, section, "edit");
  const canCreate = can(user, section, "create") || canEdit;
  const focusId = searchParams.get("focus");
  const visiblePhysios = focusId ? physios.filter((p) => p.id === focusId) : physios;

  const load = useCallback(async () => {
    try {
      const { data: ph } = await api.get("/physios");
      setPhysios(ph.filter((p) => (p.category || "fisioterapista") === category));
      if (!isMedico) {
        const { data: sh } = await api.get("/shifts");
        setShifts(sh);
      } else {
        setShifts([]);
      }
    } catch (e) {
      toast.error(apiError(e, "Errore nel caricamento dei professionisti"));
    }
  }, [category, isMedico]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!form.first_name.trim() || !form.last_name.trim()) { toast.error("Nome e cognome obbligatori"); return; }
    try {
      const body = {
        ...form,
        category,
        default_slot_duration_minutes: Number(form.default_slot_duration_minutes || 20),
      };
      if (edit?.id) await api.put(`/physios/${edit.id}`, body);
      else await api.post("/physios", body);
      toast.success(isMedico ? "Medico salvato" : "Fisioterapista salvato");
      setEdit(null);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const del = async (p) => {
    if (!window.confirm(`Eliminare ${p.first_name} ${p.last_name}?`)) return;
    try {
      await api.delete(`/physios/${p.id}`);
      toast.success("Fisioterapista eliminato");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const saveShift = async () => {
    const body = {
      physio_id: shiftFor.id, type: shiftForm.type,
      weekday: shiftForm.recurring ? Number(shiftForm.weekday) : null,
      date: shiftForm.recurring ? null : shiftForm.date || null,
      start: shiftForm.start, end: shiftForm.end, note: shiftForm.note,
    };
    if (!shiftForm.recurring && !body.date) { toast.error("Seleziona una data"); return; }
    try {
      await api.post("/shifts", body);
      toast.success("Turno salvato");
      setShiftForm(EMPTY_SHIFT);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const delShift = async (sid) => {
    try {
      await api.delete(`/shifts/${sid}`);
      toast.success("Turno eliminato");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const shiftsOf = (pid) => shifts.filter((s) => s.physio_id === pid);

  return (
    <div className="space-y-4" data-testid="physios-page">
      <div className="flex items-center gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold">{isMedico ? "Medici — Ambulatorio" : "Fisioterapisti"}</h1>
          {isMedico && <p className="text-sm text-slate-500 mt-1">Configura il medico e le giornate con slot prenotabili. I turni dei fisioterapisti restano separati.</p>}
        </div>
        {canCreate && (
          <button data-testid="physio-new" onClick={() => { setForm(EMPTY); setEdit({}); }} className="ml-auto flex items-center gap-1.5 bg-blue-600 text-white px-4 py-2.5 rounded-full text-sm font-medium"><Plus size={16} /> {isMedico ? "Medico" : "Fisioterapista"}</button>
        )}
      </div>

      {focusId && visiblePhysios.length > 0 && (
        <div className="flex justify-end">
          <button
            type="button"
            onClick={() => setSearchParams({})}
            className="text-sm text-blue-600 hover:underline"
            data-testid="show-all-professionals"
          >
            Mostra tutti
          </button>
        </div>
      )}

      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
        {visiblePhysios.map((p) => (
          <div key={p.id} data-testid={`physio-card-${p.id}`} className={`bg-white rounded-2xl border border-slate-100 p-5 ${!p.active ? "opacity-60" : ""}`}>
            <div className="flex items-center gap-3">
              <span className="w-11 h-11 rounded-2xl flex items-center justify-center text-white font-bold" style={{ background: p.color }}>{p.first_name[0]}{p.last_name[0]}</span>
              <div className="flex-1 min-w-0">
                <p className="font-heading font-bold truncate">{p.first_name} {p.last_name}</p>
                <p className="text-xs text-slate-400 truncate">{p.specialization || (isMedico ? "Medico" : "Fisioterapista")}</p>
              </div>
              {!p.active && <span className="text-xs bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full">Non attivo</span>}
            </div>
            <p className="text-xs text-slate-500 mt-2">{p.phone || ""} {p.email ? `· ${p.email}` : ""}</p>

            {isMedico ? (
              <div className="mt-3 flex items-center gap-2 text-xs text-slate-500">
                <Clock size={13} className="text-blue-500" />
                Slot predefinito <span className="font-semibold text-slate-700">{p.default_slot_duration_minutes || 20} min</span>
              </div>
            ) : (
              <div className="flex flex-wrap gap-1.5 mt-3">
                {shiftsOf(p.id).filter((s) => s.type === "turno").map((s) => (
                  <span key={s.id} className="text-[10px] bg-slate-50 border border-slate-100 rounded-full px-2 py-0.5 text-slate-500">
                    {s.weekday !== null && s.weekday !== undefined ? WEEKDAYS[s.weekday].slice(0, 3) : s.date} {s.start}–{s.end}
                  </span>
                ))}
              </div>
            )}

            <div className="flex gap-2 mt-4">
              <button data-testid={`physio-calendar-${p.id}`} onClick={() => navigate("/calendario")} className="flex items-center gap-1 text-xs text-blue-600 hover:underline"><CalendarDays size={13} /> Calendario</button>
              {canEdit && <>
                <button data-testid={`physio-shifts-${p.id}`} onClick={() => { setShiftFor(shiftFor?.id === p.id ? null : p); setShiftForm(EMPTY_SHIFT); }} className="flex items-center gap-1 text-xs text-cyan-600 hover:underline"><Clock size={13} /> {isMedico ? "Slot / giornate" : "Turni"}</button>
                <button data-testid={`physio-edit-${p.id}`} onClick={() => { setForm({ ...EMPTY, ...p, default_slot_duration_minutes: p.default_slot_duration_minutes || 20 }); setEdit(p); }} className="flex items-center gap-1 text-xs text-slate-500 hover:underline"><Pencil size={13} /> Modifica</button>
              </>}
              {!isMedico && can(user, "fisioterapisti", "delete") && <button data-testid={`physio-delete-${p.id}`} onClick={() => del(p)} className="flex items-center gap-1 text-xs text-red-400 hover:underline ml-auto"><Trash2 size={13} /></button>}
            </div>

            {shiftFor?.id === p.id && isMedico && (
              <div className="mt-4 border-t border-slate-100 pt-4">
                <MedicalAvailabilityPanel doctor={p} canEdit={canEdit} />
              </div>
            )}

            {shiftFor?.id === p.id && !isMedico && (
              <div className="mt-4 border-t border-slate-100 pt-3 space-y-3" data-testid={`shifts-panel-${p.id}`}>
                <p className="text-[10px] uppercase tracking-widest text-slate-400">Turni, assenze e ferie</p>
                <div className="space-y-1">
                  {shiftsOf(p.id).map((s) => (
                    <div key={s.id} className="flex items-center gap-2 text-xs bg-slate-50 rounded-lg px-2.5 py-1.5">
                      <span className={`font-medium ${s.type === "turno" ? "text-blue-600" : "text-amber-600"}`}>{s.type}</span>
                      <span className="text-slate-600">{s.weekday !== null && s.weekday !== undefined ? `${WEEKDAYS[s.weekday]} (ricorrente)` : s.date}</span>
                      {s.type === "turno" && <span className="text-slate-400 font-mono">{s.start}–{s.end}</span>}
                      {s.note && <span className="text-slate-400 italic">{s.note}</span>}
                      <button data-testid={`shift-delete-${s.id}`} onClick={() => delShift(s.id)} className="ml-auto text-slate-300 hover:text-red-500"><Trash2 size={12} /></button>
                    </div>
                  ))}
                  {shiftsOf(p.id).length === 0 && <p className="text-xs text-slate-400">Nessun turno configurato.</p>}
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <Select value={shiftForm.type} onValueChange={(v) => setShiftForm({ ...shiftForm, type: v })}>
                    <SelectTrigger data-testid="shift-type" className="rounded-xl h-9 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="turno">Turno</SelectItem>
                      <SelectItem value="assenza">Assenza</SelectItem>
                      <SelectItem value="ferie">Ferie</SelectItem>
                    </SelectContent>
                  </Select>
                  <div className="flex items-center gap-2 text-xs">
                    <Switch data-testid="shift-recurring" checked={shiftForm.recurring} onCheckedChange={(c) => setShiftForm({ ...shiftForm, recurring: c })} />
                    Ricorrente
                  </div>
                  {shiftForm.recurring ? (
                    <Select value={shiftForm.weekday} onValueChange={(v) => setShiftForm({ ...shiftForm, weekday: v })}>
                      <SelectTrigger data-testid="shift-weekday" className="rounded-xl h-9 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>{WEEKDAYS.map((d, i) => <SelectItem key={i} value={String(i)}>{d}</SelectItem>)}</SelectContent>
                    </Select>
                  ) : (
                    <Input data-testid="shift-date" type="date" value={shiftForm.date} onChange={(e) => setShiftForm({ ...shiftForm, date: e.target.value })} className="rounded-xl h-9 text-xs" />
                  )}
                  {shiftForm.type === "turno" ? (
                    <div className="flex gap-1 items-center">
                      <Input data-testid="shift-start" type="time" value={shiftForm.start} onChange={(e) => setShiftForm({ ...shiftForm, start: e.target.value })} className="rounded-xl h-9 text-xs" />
                      <Input data-testid="shift-end" type="time" value={shiftForm.end} onChange={(e) => setShiftForm({ ...shiftForm, end: e.target.value })} className="rounded-xl h-9 text-xs" />
                    </div>
                  ) : <div />}
                  <Input data-testid="shift-note" placeholder="Nota (es. sostituzione)" value={shiftForm.note} onChange={(e) => setShiftForm({ ...shiftForm, note: e.target.value })} className="rounded-xl h-9 text-xs col-span-2" />
                </div>
                <Button size="sm" data-testid="shift-save" onClick={saveShift} className="rounded-full w-full">Aggiungi turno</Button>
              </div>
            )}
          </div>
        ))}
      </div>

      <Dialog open={Boolean(edit)} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="max-w-md rounded-2xl" data-testid="physio-modal">
          <DialogHeader><DialogTitle className="font-heading text-xl">{edit?.id ? (isMedico ? "Modifica medico" : "Modifica fisioterapista") : (isMedico ? "Nuovo medico" : "Nuovo fisioterapista")}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Nome *</Label><Input data-testid="physio-first-name" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className="rounded-xl" /></div>
            <div><Label>Cognome *</Label><Input data-testid="physio-last-name" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} className="rounded-xl" /></div>
            <div><Label>Telefono</Label><Input data-testid="physio-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="rounded-xl" /></div>
            <div><Label>Email</Label><Input data-testid="physio-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-xl" /></div>
            <div className="col-span-2"><Label>{isMedico ? "Specializzazione (es. Ortopedico)" : "Specializzazione"}</Label><Input data-testid="physio-specialization" value={form.specialization} onChange={(e) => setForm({ ...form, specialization: e.target.value })} className="rounded-xl" /></div>
            {isMedico && (
              <div className="col-span-2">
                <Label>Durata slot predefinita</Label>
                <Select value={String(form.default_slot_duration_minutes || 20)} onValueChange={(v) => setForm({ ...form, default_slot_duration_minutes: Number(v) })}>
                  <SelectTrigger data-testid="physio-default-slot-duration" className="rounded-xl"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {[15, 20, 30, 40, 45, 60].map((d) => <SelectItem key={d} value={String(d)}>{d} minuti</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="col-span-2">
              <Label>Colore calendario</Label>
              <div className="flex gap-2 mt-1.5 flex-wrap" data-testid="physio-colors">
                {COLORS.map((c) => (
                  <button key={c} data-testid={`physio-color-${c}`} onClick={() => setForm({ ...form, color: c })}
                    className={`w-8 h-8 rounded-full transition-transform ${form.color === c ? "ring-2 ring-offset-2 ring-blue-400 scale-110" : ""}`} style={{ background: c }} />
                ))}
              </div>
            </div>
            <div className="flex items-center gap-2"><Switch data-testid="physio-active" checked={form.active} onCheckedChange={(c) => setForm({ ...form, active: c })} /><Label>Attivo</Label></div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" data-testid="physio-cancel" onClick={() => setEdit(null)} className="rounded-full">Annulla</Button>
            <Button data-testid="physio-save" onClick={save} className="rounded-full">Salva</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
